import { useEffect, useRef } from 'react';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { shareLink } from '../../application/pwa/shareLink';
import { plantShareUrl, sharePlantCard } from '../../application/pwa/sharePlantCard';
import { sharePlantSheet } from '../../application/pwa/sharePlantSheet';
import { plantShareMessage } from '../../domain/plants/plantShare';
import { LinkIcon, PictureIcon, PostIcon, SheetIcon } from '../icons/ControlIcons';

/**
 * How this plant travels (SPEC-019 D-3).
 *
 * The share glyph used to do one thing, and the thing it did was right for one
 * destination out of three. A dense A4 sheet is what a gardener wants in their
 * Photos roll and is unreadable in a feed; a scenic card is what a feed wants
 * and says nothing a gardener could plan by; an address is worth more than
 * either in the places that unfurl one, and worth nothing in Instagram, which
 * takes no links at all.
 *
 * One tap more, and the reader chooses the destination rather than the app
 * guessing at it. The rows are ordered by what people actually do with a plant —
 * post it, keep it, send it — and each says where it goes rather than what the
 * button does.
 *
 * Two of the four rows are the same card in different rectangles (PR-19.8), and
 * that is a real distinction rather than a setting: 4:5 is what a story and a
 * message bubble render whole, and it is what a Facebook feed letterboxes down
 * to a column. A destination chooser that offered a shape picker instead would
 * be asking the reader to know that. The row names the destination; the shape is
 * this file's problem.
 *
 * A real `<dialog>` opened with `showModal()`, for the reason SPEC-006 D-3 gives
 * and `FilterSheet` follows: focus trapping, Escape, the top layer and an inert
 * background are four things the platform gets right and a hand-rolled bottom
 * sheet gets subtly wrong.
 */

/**
 * One destination.
 *
 * `onChoose` is `async` and the row closes the sheet only after it settles, so
 * the OS share sheet opens over this one rather than under a dialog that is
 * already tearing down — which on iOS is the difference between a share sheet
 * and nothing happening at all.
 */
function ShareChoice({ Glyph, label, note, onChoose }) {
    return (
        <button type="button" className="share-choice" onClick={onChoose}>
            <span className="share-choice__glyph" aria-hidden="true"><Glyph size={24} /></span>
            <span className="share-choice__text">
                <span className="share-choice__label">{label}</span>
                <span className="share-choice__note">{note}</span>
            </span>
        </button>
    );
}

export default function PlantShareSheet({ plant, commonName, tagline, onResult, onClose }) {
    const { t, localeCode, languageCode } = useLocale();
    const dialogRef = useRef(null);

    // Opening is a one-off, and deliberately its own effect — the same split
    // `FilterSheet` makes, for the same reason: an effect that depended on a
    // handler would close and re-open the dialog on every re-render, and the
    // second `showModal()` throws.
    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.showModal();

        return () => {
            if (dialog.open) {
                dialog.close();
            }
        };
    }, []);

    // One exit for every way out: a choice closes it, and so do Escape and a tap
    // on the backdrop, which the browser turns into the same event.
    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.addEventListener('close', onClose);

        return () => dialog.removeEventListener('close', onClose);
    }, [onClose]);

    const { title, text } = plantShareMessage({ commonName, tagline }, t);
    // The canonical address, not `window.location.href`: on a Firebase mirror or
    // a preview channel the current href is not the product's address, and it is
    // also the address the plant's own social document is served at (D-8).
    const url = plantShareUrl(localeCode, plant.id, typeof window === 'undefined' ? null : window.location);
    const payload = { plantId: plant.id, commonName, title, text, url };

    const choose = (share) => async () => {
        const result = await share();

        onResult(result);
        dialogRef.current?.close();
    };

    return (
        <dialog
            className="share-sheet"
            ref={dialogRef}
            aria-labelledby="share-sheet-title"
            // A click that lands on the dialog element itself landed on the
            // backdrop: everything inside is a child.
            onClick={(event) => {
                if (event.target === dialogRef.current) {
                    dialogRef.current.close();
                }
            }}
        >
            <span className="share-sheet__grip" aria-hidden="true" />

            <h2 className="share-sheet__title" id="share-sheet-title">{t('plants.share.choose')}</h2>

            <div className="share-sheet__choices">
                <ShareChoice
                    Glyph={PictureIcon}
                    label={t('plants.share.asCard')}
                    note={t('plants.share.asCardNote')}
                    onChoose={choose(() => sharePlantCard({ ...payload, languageCode }))}
                />
                <ShareChoice
                    Glyph={PostIcon}
                    label={t('plants.share.asPost')}
                    note={t('plants.share.asPostNote')}
                    onChoose={choose(() => sharePlantCard({ ...payload, languageCode, shape: 'landscape' }))}
                />
                <ShareChoice
                    Glyph={SheetIcon}
                    label={t('plants.share.asSheet')}
                    note={t('plants.share.asSheetNote')}
                    onChoose={choose(() => sharePlantSheet({ ...payload, languageCode }))}
                />
                <ShareChoice
                    Glyph={LinkIcon}
                    label={t('plants.share.asLink')}
                    note={t('plants.share.asLinkNote')}
                    onChoose={choose(() => shareLink({ title, text, url }))}
                />
            </div>

            <button
                type="button"
                className="button share-sheet__cancel"
                onClick={() => dialogRef.current.close()}
            >
                {t('plants.share.cancel')}
            </button>
        </dialog>
    );
}
